import type { ComponentConfig } from '@puckeditor/core';
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { Reviews } from './reviews';
import { ReviewsFields } from "./reviews.fields";
import type { ReviewsProps } from './reviews';
import { generatedSubheading } from "../../lib/generated-subheading";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { metadataIsOnePage } from "../../lib/one-page-metadata";

const ReviewsConfig: ComponentConfig<ReviewsProps> = {
  defaultProps: {
    heading: "What Our Customers Say",
    layout: "grid",
    minRating: 4,
    reviewLimit: 6,
    reviews: [
      {
        date: "2024-01-15",
        location: "Phoenix, AZ",
        name: "Sarah Johnson",
        rating: 5,
        text: "Exceptional service! They arrived on time, diagnosed the problem quickly, and had it fixed within an hour. Professional and courteous.",
      },
      {
        date: "2024-01-10",
        location: "Phoenix, AZ",
        name: "Michael Chen",
        rating: 5,
        text: "Best decision we made was signing up for their maintenance plan. No more surprise breakdowns and energy bills have decreased significantly. Fair pricing and excellent work.",
      },
      {
        date: "2024-01-05",
        location: "Phoenix, AZ",
        name: "Jennifer Martinez",
        rating: 5,
        text: "Called them for an emergency repair on a Sunday. They came within 2 hours and fixed our system fast and fixed our system. Fair pricing and excellent work.",
      },
    ],
    showDates: "yes",
    showRatings: "yes",
    subheading: "See what our satisfied customers have to say about working with us",
    useRealReviews: "yes",
  },
  fields: {
    heading: {
      ...ReviewsFields.heading,
      contentEditable: true,
      render: ({
        value,
        onChange,
        field,
      }: {
        value: any;
        onChange: (val: any) => void;
        field: any;
      }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.reviews.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ReviewsFields.subheading,
      contentEditable: true,
      render: ({
        value,
        onChange,
        field,
      }: {
        value: any;
        onChange: (val: any) => void;
        field: any;
      }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.reviews.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: ReviewsFields.layout,
    variant: ReviewsFields.variant,
    showRatings: ReviewsFields.showRatings,
    showDates: ReviewsFields.showDates,
    useRealReviews: ReviewsFields.useRealReviews,
    reviewLimit: ReviewsFields.reviewLimit,
    minRating: ReviewsFields.minRating,
    reviews: ReviewsFields.reviews,
    backgroundColor: ReviewsFields.backgroundColor,
  },
  label: "Reviews",
  render: ({ id, puck, editMode, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as any,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business,
      market = business?.primaryMarket,

      // Pass siteId and apiUrl directly from metadata — avoids iframe timing issues
      // Where window.__SERVICEPRO_CONFIG__ isn't set yet on first render
      puckSiteId = puckMetadata?.site?.id as string | undefined,
      puckApiUrl = puckMetadata?.apiUrl as string | undefined,

      resolvedProps = {
        ...props,
        subheading:
          props.subheading ||
          generatedSubheading(
            props.variant,
            market
              ? `See what your neighbors in ${market.name} are saying about us.`
              : "See what our customers are saying about us.",
            metadataIsOnePage(puck?.metadata),
          ),
      },

      // The business profile's Google count paints the badge first; the API's total,
      // Which adds every other platform, replaces it once reviews load.
      totalReviewCount: number | undefined =
        typeof business?.googleReviewCount === "number" ? business.googleReviewCount : undefined;

    return (
      <Reviews
        id={id}
        {...resolvedProps}
        puckSiteId={puckSiteId}
        puckApiUrl={puckApiUrl}
        totalReviewCount={totalReviewCount}
        rating={typeof business?.googleRating === "number" ? business.googleRating : undefined}
        // The Simple UI canvas renders through plain <Render>, so editor-ness
        // Comes from the window config there, as EmergencyBand resolves it.
        isEditing={Boolean(windowConfig?.isEditing ?? editMode)}
      />
    );
  },
  resolveFields: (data, { fields }) => {
    // Hide manual reviews array when using real reviews from the database.
    // Absent means "yes" — generator-authored blocks omit the prop and the
    // Component defaults to fetching real reviews.
    if ((data.props as any).useRealReviews !== "no") {
      const { reviews: _reviews, ...rest } = fields as any;
      return rest;
    }

    // On the manual list, the database-query controls have nothing to query.
    const visible = { ...(fields as any) };
    delete visible.reviewLimit;
    delete visible.minRating;

    // And a per-review rating is not read when ratings are hidden.
    if ((data.props as any).showRatings === "no" && visible.reviews?.arrayFields?.rating) {
      const { rating: _rating, ...arrayFields } = visible.reviews.arrayFields;
      visible.reviews = { ...visible.reviews, arrayFields };
    }

    return visible;
  },
};

export { ReviewsConfig };
